import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { iconMapping } from 'services/utils/iconMapping';
import KnowledgeBaseLayout from './KnowledgeBaseLayout';
import { KnowledgeBaseArticleCard, KnowledgeBaseAuthorsLine } from './KnowledgeBaseCards';
import { fetchPublicKnowledgeBase } from './knowledgeBaseApi';
import { PublicKnowledgeBaseGroup, KNOWLEDGE_BASE_PUBLIC_PATH, groupIconKey } from './KnowledgeBaseTypes';

// The page of a group in the public knowledge base, sections as titles above full width article cards
const KnowledgeBaseGroupPage: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { id } = useParams<{ id: string }>();
    const [group, setGroup] = useState<PublicKnowledgeBaseGroup | null>(null);
    const [notFound, setNotFound] = useState(false);

    // Fetch the group with its sections
    useEffect(() => {
        setGroup(null);
        setNotFound(false);
        fetchPublicKnowledgeBase<PublicKnowledgeBaseGroup>(`get_public_knowledge_base_group/${id}`)
            .then(setGroup)
            .catch(() => setNotFound(true));
    }, [id]);


    return (
        <KnowledgeBaseLayout>
            <nav className='kb-breadcrumb'>
                <Link to={KNOWLEDGE_BASE_PUBLIC_PATH}>{t('knowledge_base.public.all_groups')}</Link>
                {group && <><span className='kb-breadcrumb-separator'>/</span><span>{group.name}</span></>}
            </nav>

            {notFound && <div className='kb-empty-state'>{t('knowledge_base.public.not_found')}</div>}

            {group && (
                <>
                    <div className='kb-group-header'>
                        <div className='kb-group-icon'><FontAwesomeIcon icon={iconMapping(groupIconKey(group.icon))} /></div>
                        <div className='kb-group-header-text'>
                            <h1>{group.name}</h1>
                            {group.description && <p>{group.description}</p>}
                            <KnowledgeBaseAuthorsLine authors={group.authors ?? []} articleCount={group.article_count} />
                        </div>
                    </div>

                    {group.sections.map(section => (
                        <div className='kb-section' key={section.id ?? 'none'}>
                            {section.name && <h2 className='kb-section-title'>{section.name}</h2>}
                            <div className='kb-card-list'>
                                {section.articles.map(article => (
                                    <KnowledgeBaseArticleCard
                                        key={article.id}
                                        article={article}
                                        onClick={() => history.push(`${KNOWLEDGE_BASE_PUBLIC_PATH}/artikel/${article.id}`)} />
                                ))}
                            </div>
                        </div>
                    ))}

                    {group.sections.length === 0 && <div className='kb-empty-state'>{t('knowledge_base.public.no_articles')}</div>}
                </>
            )}
        </KnowledgeBaseLayout>
    );
};

export default KnowledgeBaseGroupPage;
